import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useTenant } from "../lib/tenant-context";
import { tenantFetch } from "../lib/api-client";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";

interface AnalyticsData {
  metrics: {
    footfall: number;
    footfallChange: number;
    conversions: number;
    conversionRate: number;
    conversionChange: number;
    avgTradeInValue: number;
    valueChange: number;
    totalValue: number;
  };
  dailyData: {
    date: string;
    footfall: number;
    conversions: number;
    value: number;
  }[];
  categoryBreakdown: {
    category: string;
    count: number;
    value: number;
    percentage: number;
  }[];
  conditionBreakdown: {
    condition: string;
    count: number;
    percentage: number;
  }[];
  customerSegments: {
    segment: string;
    count: number;
    avgValue: number;
    color: string;
  }[];
  topProducts: { name: string; count: number; avgValue: number }[];
  locationPerformance: {
    location: string;
    footfall: number;
    conversions: number;
    rate: number;
  }[];
}

export const Route = createFileRoute("/trade-in-analytics")({
  component: TradeInAnalyticsDashboard,
});

function TradeInAnalyticsDashboard() {
  const { currentTenant } = useTenant();
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<"7d" | "30d" | "90d">("30d");

  useEffect(() => {
    loadAnalytics();
  }, [currentTenant.id, period]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const res = await tenantFetch(
        `/api/v1/analytics/trade-in?period=${period}`,
      );
      if (res.ok) {
        const result = await res.json();
        setData(result);
      } else {
        setData(getDemoAnalytics(period));
      }
    } catch (error) {
      console.error("Failed to load analytics:", error);
      setData(getDemoAnalytics(period));
    } finally {
      setLoading(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{
            borderColor: currentTenant.color,
            borderTopColor: "transparent",
          }}
        />
      </div>
    );
  }

  const COLORS = [
    "#00FBA9",
    currentTenant.color,
    "#3B82F6",
    "#8B5CF6",
    "#F59E0B",
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light" style={{ color: "#171717" }}>
            Trade-In Analytics
          </h1>
          <p className="text-sm mt-1" style={{ color: "#737373" }}>
            Performance metrics and customer insights for your trade-in program
          </p>
        </div>
        <div className="flex gap-2">
          {(["7d", "30d", "90d"] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2  text-sm font-light transition-colors ${
                period === p ? "text-white" : "border"
              }`}
              style={{
                backgroundColor:
                  period === p ? currentTenant.color : "transparent",
                borderColor: period !== p ? "#E5E5E5" : "transparent",
                color: period === p ? "#fff" : "#525252",
              }}
            >
              {p === "7d" ? "7 Days" : p === "30d" ? "30 Days" : "90 Days"}
            </button>
          ))}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          label="Footfall"
          value={data.metrics.footfall.toLocaleString()}
          change={`${data.metrics.footfallChange > 0 ? "+" : ""}${data.metrics.footfallChange}%`}
          positive={data.metrics.footfallChange > 0}
          subtext="Trade-in inquiries"
          color={currentTenant.color}
        />
        <MetricCard
          label="Conversions"
          value={data.metrics.conversions.toLocaleString()}
          change={`${data.metrics.conversionRate}% rate`}
          positive={data.metrics.conversionChange > 0}
          subtext="Completed trade-ins"
          color="#00FBA9"
        />
        <MetricCard
          label="Avg Value"
          value={`$${data.metrics.avgTradeInValue}`}
          change={`${data.metrics.valueChange > 0 ? "+" : ""}${data.metrics.valueChange}%`}
          positive={data.metrics.valueChange > 0}
          subtext="Per trade-in"
          color="#3B82F6"
        />
        <MetricCard
          label="Total Value"
          value={`$${(data.metrics.totalValue / 1000).toFixed(1)}K`}
          change="Credit issued"
          positive={true}
          subtext="This period"
          color="#8B5CF6"
        />
      </div>

      {/* Trend Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Footfall & Conversions */}
        <div
          className=" p-6"
          style={{ backgroundColor: "#FAFAFA", border: "1px solid #E5E5E5" }}
        >
          <h2 className="text-lg font-light mb-4" style={{ color: "#171717" }}>
            Footfall & Conversions
          </h2>
          <div className="h-64">
            <ResponsiveContainer
              width="100%"
              height="100%"
              minWidth={100}
              minHeight={100}
            >
              <AreaChart data={data.dailyData}>
                <defs>
                  <linearGradient id="footfallGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="5%"
                      stopColor={currentTenant.color}
                      stopOpacity={0.3}
                    />
                    <stop
                      offset="95%"
                      stopColor={currentTenant.color}
                      stopOpacity={0}
                    />
                  </linearGradient>
                  <linearGradient
                    id="conversionGrad"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#00FBA9" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#00FBA9" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  stroke="#737373"
                />
                <YAxis tick={{ fontSize: 11 }} stroke="#737373" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#171717",
                    border: "none",
                    borderRadius: "8px",
                  }}
                  labelStyle={{ color: "#737373" }}
                  itemStyle={{ color: "#fff" }}
                />
                <Area
                  type="monotone"
                  dataKey="footfall"
                  stroke={currentTenant.color}
                  strokeWidth={2}
                  fill="url(#footfallGrad)"
                  name="Inquiries"
                />
                <Area
                  type="monotone"
                  dataKey="conversions"
                  stroke="#00FBA9"
                  strokeWidth={2}
                  fill="url(#conversionGrad)"
                  name="Conversions"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Trade-In Value Trend */}
        <div
          className=" p-6"
          style={{ backgroundColor: "#FAFAFA", border: "1px solid #E5E5E5" }}
        >
          <h2 className="text-lg font-light mb-4" style={{ color: "#171717" }}>
            Trade-In Value Trend
          </h2>
          <div className="h-64">
            <ResponsiveContainer
              width="100%"
              height="100%"
              minWidth={100}
              minHeight={100}
            >
              <LineChart data={data.dailyData}>
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  stroke="#737373"
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  stroke="#737373"
                  tickFormatter={(v) => `$${v}`}
                />
                <Tooltip
                  formatter={(value) => [`$${value ?? 0}`, "Value"]}
                  contentStyle={{
                    backgroundColor: "#171717",
                    border: "none",
                    borderRadius: "8px",
                  }}
                  labelStyle={{ color: "#737373" }}
                  itemStyle={{ color: "#fff" }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#3B82F6"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Category Breakdown */}
        <div
          className=" p-6"
          style={{ backgroundColor: "#FAFAFA", border: "1px solid #E5E5E5" }}
        >
          <h2 className="text-lg font-light mb-4" style={{ color: "#171717" }}>
            By Category
          </h2>
          <div className="h-48 flex items-center justify-center">
            <ResponsiveContainer
              width="100%"
              height="100%"
              minWidth={100}
              minHeight={100}
            >
              <PieChart>
                <Pie
                  data={data.categoryBreakdown}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey="count"
                >
                  {data.categoryBreakdown.map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number | undefined) => [value ?? 0, "Count"]}
                  contentStyle={{
                    backgroundColor: "#171717",
                    border: "none",
                    borderRadius: "8px",
                  }}
                  itemStyle={{ color: "#fff" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2 mt-4">
            {data.categoryBreakdown.map((cat, i) => (
              <div
                key={cat.category}
                className="flex items-center justify-between text-sm"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: COLORS[i % COLORS.length] }}
                  />
                  <span style={{ color: "#525252" }}>{cat.category}</span>
                </div>
                <span className="font-light" style={{ color: "#171717" }}>
                  {cat.percentage}%
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Condition Breakdown */}
        <div
          className=" p-6"
          style={{ backgroundColor: "#FAFAFA", border: "1px solid #E5E5E5" }}
        >
          <h2 className="text-lg font-light mb-4" style={{ color: "#171717" }}>
            By Condition
          </h2>
          <div className="space-y-4">
            {data.conditionBreakdown.map((cond) => (
              <div key={cond.condition}>
                <div className="flex justify-between text-sm mb-1">
                  <span style={{ color: "#525252" }}>{cond.condition}</span>
                  <span className="font-light" style={{ color: "#171717" }}>
                    {cond.count} ({cond.percentage}%)
                  </span>
                </div>
                <div className="w-full h-2 rounded-full bg-gray-200">
                  <div
                    className="h-2 rounded-full"
                    style={{
                      width: `${cond.percentage}%`,
                      backgroundColor:
                        cond.condition === "Excellent"
                          ? "#00FBA9"
                          : cond.condition === "Like New"
                            ? "#3B82F6"
                            : cond.condition === "Good"
                              ? "#F59E0B"
                              : "#EF4444",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Customer Segments */}
        <div
          className=" p-6"
          style={{ backgroundColor: "#FAFAFA", border: "1px solid #E5E5E5" }}
        >
          <h2 className="text-lg font-light mb-4" style={{ color: "#171717" }}>
            Customer Segments
          </h2>
          <div className="space-y-4">
            {data.customerSegments.map((seg) => (
              <div
                key={seg.segment}
                className="p-3  bg-white border"
                style={{ borderColor: "#E5E5E5" }}
              >
                <div className="flex items-center justify-between mb-1">
                  <span
                    className="font-light text-sm"
                    style={{ color: "#171717" }}
                  >
                    {seg.segment}
                  </span>
                  <span
                    className="text-xs px-2 py-1 rounded"
                    style={{
                      backgroundColor: seg.color + "20",
                      color: seg.color,
                    }}
                  >
                    {seg.count} users
                  </span>
                </div>
                <div className="text-xs" style={{ color: "#737373" }}>
                  Avg trade-in: ${seg.avgValue}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Location Performance & Top Products */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Location Performance */}
        <div
          className=" p-6"
          style={{ backgroundColor: "#FAFAFA", border: "1px solid #E5E5E5" }}
        >
          <h2 className="text-lg font-light mb-4" style={{ color: "#171717" }}>
            Location Performance
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left">
                  <th
                    className="pb-3 text-xs font-light uppercase"
                    style={{ color: "#737373" }}
                  >
                    Location
                  </th>
                  <th
                    className="pb-3 text-xs font-light uppercase text-right"
                    style={{ color: "#737373" }}
                  >
                    Footfall
                  </th>
                  <th
                    className="pb-3 text-xs font-light uppercase text-right"
                    style={{ color: "#737373" }}
                  >
                    Conversions
                  </th>
                  <th
                    className="pb-3 text-xs font-light uppercase text-right"
                    style={{ color: "#737373" }}
                  >
                    Rate
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: "#E5E5E5" }}>
                {data.locationPerformance.map((loc) => (
                  <tr key={loc.location}>
                    <td className="py-3 text-sm" style={{ color: "#171717" }}>
                      {loc.location}
                    </td>
                    <td
                      className="py-3 text-sm text-right"
                      style={{ color: "#525252" }}
                    >
                      {loc.footfall}
                    </td>
                    <td
                      className="py-3 text-sm text-right"
                      style={{ color: "#525252" }}
                    >
                      {loc.conversions}
                    </td>
                    <td
                      className="py-3 text-sm text-right font-light"
                      style={{ color: loc.rate >= 30 ? "#16A34A" : "#171717" }}
                    >
                      {loc.rate}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Products */}
        <div
          className=" p-6"
          style={{ backgroundColor: "#FAFAFA", border: "1px solid #E5E5E5" }}
        >
          <h2 className="text-lg font-light mb-4" style={{ color: "#171717" }}>
            Top Trade-In Products
          </h2>
          <div className="space-y-3">
            {data.topProducts.map((product, i) => (
              <div
                key={product.name}
                className="flex items-center gap-4 p-3  bg-white border"
                style={{ borderColor: "#E5E5E5" }}
              >
                <div
                  className="w-8 h-8  flex items-center justify-center text-sm font-light text-white"
                  style={{
                    backgroundColor:
                      i === 0
                        ? "#00FBA9"
                        : i === 1
                          ? currentTenant.color
                          : "#737373",
                  }}
                >
                  {i + 1}
                </div>
                <div className="flex-1">
                  <div
                    className="font-light text-sm"
                    style={{ color: "#171717" }}
                  >
                    {product.name}
                  </div>
                  <div className="text-xs" style={{ color: "#737373" }}>
                    {product.count} trade-ins
                  </div>
                </div>
                <div className="text-right">
                  <div
                    className="font-light text-sm"
                    style={{ color: "#171717" }}
                  >
                    ${product.avgValue}
                  </div>
                  <div className="text-xs" style={{ color: "#737373" }}>
                    avg value
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({
  label,
  value,
  change,
  positive,
  subtext,
  color,
}: {
  label: string;
  value: string;
  change: string;
  positive: boolean;
  subtext: string;
  color: string;
}) {
  return (
    <div
      className=" p-5"
      style={{ backgroundColor: "#FAFAFA", border: "1px solid #E5E5E5" }}
    >
      <div
        className="text-xs font-light uppercase mb-2"
        style={{ color: "#737373" }}
      >
        {label}
      </div>
      <div className="text-2xl font-semibold mb-1" style={{ color }}>
        {value}
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs" style={{ color: "#737373" }}>
          {subtext}
        </span>
        <span
          className="text-xs font-light"
          style={{
            color: positive
              ? "#16A34A"
              : change.includes("%")
                ? "#DC2626"
                : "#737373",
          }}
        >
          {change}
        </span>
      </div>
    </div>
  );
}

function getDemoAnalytics(period: string): AnalyticsData {
  const days = period === "7d" ? 7 : period === "30d" ? 30 : 90;
  const dailyData = [];

  for (let i = days; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    dailyData.push({
      date: date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
      footfall: Math.floor(30 + Math.random() * 20),
      conversions: Math.floor(10 + Math.random() * 8),
      value: Math.floor(100 + Math.random() * 50),
    });
  }

  return {
    metrics: {
      footfall: days === 7 ? 287 : days === 30 ? 1245 : 3721,
      footfallChange: 12,
      conversions: days === 7 ? 89 : days === 30 ? 412 : 1156,
      conversionRate: 31,
      conversionChange: 4,
      avgTradeInValue: 127,
      valueChange: 8,
      totalValue: days === 7 ? 11303 : days === 30 ? 52324 : 146812,
    },
    dailyData,
    categoryBreakdown: [
      { category: "Shell Jackets", count: 156, value: 22620, percentage: 38 },
      { category: "Insulated", count: 124, value: 11780, percentage: 30 },
      { category: "Pants", count: 78, value: 4290, percentage: 19 },
      { category: "Packs", count: 54, value: 1890, percentage: 13 },
    ],
    conditionBreakdown: [
      { condition: "Excellent", count: 89, percentage: 22 },
      { condition: "Like New", count: 156, percentage: 38 },
      { condition: "Good", count: 134, percentage: 32 },
      { condition: "Fair", count: 33, percentage: 8 },
    ],
    customerSegments: [
      {
        segment: "Repeat Traders",
        count: 234,
        avgValue: 156,
        color: "#00FBA9",
      },
      {
        segment: "First-Time Users",
        count: 178,
        avgValue: 98,
        color: "#3B82F6",
      },
      { segment: "High-Value", count: 67, avgValue: 245, color: "#8B5CF6" },
      { segment: "Seasonal", count: 45, avgValue: 112, color: "#F59E0B" },
    ],
    topProducts: [
      { name: "Beta AR Jacket", count: 67, avgValue: 145 },
      { name: "Atom LT Hoody", count: 54, avgValue: 72 },
      { name: "Alpha SV Jacket", count: 43, avgValue: 198 },
      { name: "Gamma LT Pant", count: 38, avgValue: 52 },
      { name: "Cerium LT Hoody", count: 31, avgValue: 95 },
    ],
    locationPerformance: [
      {
        location: "Vancouver Flagship",
        footfall: 312,
        conversions: 112,
        rate: 36,
      },
      { location: "Seattle Store", footfall: 245, conversions: 78, rate: 32 },
      { location: "Portland Store", footfall: 198, conversions: 56, rate: 28 },
      { location: "Toronto Store", footfall: 178, conversions: 52, rate: 29 },
      { location: "New York Store", footfall: 156, conversions: 45, rate: 29 },
    ],
  };
}
